<template>
  <div class="relative min-w-[360px] min-h-[320px]">
    <div
      class="absolute right-2 top-0 w-10 h-5 z-20 pointer-events-none flex"
      :style="{
        background: backgroundColor && backgroundColor !== 'transparent' ? backgroundColor : '#F0F5F8'
      }">
      <Download :color="textColor" class="ml-auto" />
    </div>

    <apexchart :id="chartid"
               :options="chart_options"
               :series="series"
               :height="110 * height"
               width="100%"
               @click="clickHandler" />
  </div>
</template>

<script>
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Download } from "lucide-vue-next"

export default {
  name: "Graph",
  components: {
    Download
  },
  props: {
    showDataLabels: null,
    textColor: null,
    backgroundColor: null,
    axis: {
      type: Object,
      required: false
    },
    labels: {
      type: Array,
      default: () => [0, 1, 2, 3]
    },
    chartid: {
      type: String,
      default: "chartName"
    },
    type: {
      type: String,
      required: true
    },
    chartdata: {
      type: Array,
      default: () => [110, 90, 80, 70, 60, 50, 40, 35, 30, 25, 15]
    },
    isHorizontal: {
      type: Boolean,
      default: null
    },
    hasLegend: {
      type: Boolean,
      default: true
    },
    showValues: {
      type: Boolean,
      default: true
    },
    showZoom: {
      type: Boolean,
      default: true
    },
    graphObj: {
      type: Object,
      required: true
    },
    height: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      column_colors: [],
      chart_options: {},
      formattedChartData: [],
      toFixedChartData: [],
      formattedLables: [],
      series: []
    }
  },
  computed: {
    changed() {
      return this.$store.state.ui.darkMode
    },
    isChecked() {
      return this.hasLegend
    }
  },
  watch: {
    changed() {
      this.generateColors(this.changed)
    }
  },
  created() {
    this.chartdata.map((x) => {
      if (x.data.length > 0) {
        this.formattedChartData.push(x.data.map((point) => this.formatPoint(point)))
      }
      this.toFixedChartData.push(x.data.map((point) => this.formatToFixed(point)))
      if (this.type == "radialBar") {
        this.series = utils.percentagify(x.data.map((point) => Number(this.formatToFixed(point))))
      } else if (this.type == "treemap") {
        let data_list = x.data.map((point) => this.formatToFixed(point))

        let data = this.graphObj?.datasets?.labels.map(function (e, i) {
          return { x: e, y: data_list[i] }
        })
        this.series.push({
          ...x,
          name: x.label,
          borderRadius: 13,
          minBarLength: 10,
          borderSkipped: true,
          data: data
        })
      } else {
        this.series.push({
          ...x,
          name: x.label,
          borderRadius: 13,
          minBarLength: 10,
          borderSkipped: true,
          data: x.data.map((point) => this.formatToFixed(point))
        })
      }

      this.column_colors.push(x.backgroundColor[0])
    })
    this.formattedLables = this.labels.map((point) => (Number(point) ? this.formatPoint(Number(point)) : point))
    this.createChart()
  },
  methods: {
    clickHandler(event, chartContext, config) {
      if (config.dataPointIndex != -1) {
        this.$emitter.emit(ALL_EMITTERS.GRAPH_DETAIL_MODAL, {
          graph_label_value: this.formattedLables[config.dataPointIndex],
          graph_metric: this.axis
        })
      }
    },
    formatPoint(datapoint) {
      return this.$utils.truncateNumber(datapoint)
    },
    formatToFixed(datapoint) {
      try {
        return Number(datapoint).toFixed(2)
      } catch (error) {
        return 0
      }
    },
    createChart() {
      this.chart_options = {
        title: {
          margin: 20,
          offsetX: 0,
          offsetY: 0,
          floating: false,
          text: this.graphObj.name,
          align: "center"
        },
        theme: {
          mode: this.$store.state.ui.darkMode ? "dark" : "light"
        },
        grid: {
          borderColor: "#dbdbdb"
        },
        legend: {
          showForSingleSeries: true
        },
        chart: {
          foreColor: this.textColor,
          width: 100,
          background: this.$store.state.ui.darkMode ? "#2b3c44" : "transparent",
          type: this.type,
          zoom: {
            enabled: this.showZoom
          },
          legend: {
            labels: {
              colors: this.$store.state.ui.darkMode ? "white" : this.textColor
            }
          },
          toolbar: {
            show: true,
            export: {
              csv: {
                filename: this.graphObj.name
              },
              svg: {
                filename: this.graphObj.name
              },
              png: {
                filename: this.graphObj.name
              }
            },
            autoSelected: "zoom"
          }
        },

        plotOptions: {
          radialBar: {
            distributed: true,
            hollow: {
              margin: 2,
              size: "40%"
            },

            dataLabels: {
              enabled: this.showDataLabels,
              showOn: "always",
              name: {
                show: true,
                color: "#888",
                fontSize: "13px"
              },
              value: {
                color: "#111",
                fontSize: "25px",
                show: true
              },
              total: {
                show: true,
                label: "Total",
                formatter: function () {
                  return "100%"
                }
              }
            }
          },
          bar: {
            horizontal: this.isHorizontal,
            columnWidth: "80%",
            endingShape: "rounded"
          },
          candlestick: {
            wick: {
              useFillColor: true
            }
          },
          heatmap: {
            reverseNegativeShade: true,
            distributed: true,
            horizontal: this.isHorizontal,
            columnWidth: "80%",
            endingShape: "rounded"
          },
          treemap: {
            enableShades: true,
            shadeIntensity: 0.5,
            distributed: true,
            reverseNegativeShade: true
          }
        },
        dataLabels: {
          enabled: this.type == "treemap" || this.type == "radialBar" ? true : false
        },
        stroke: {
          show: true,
          width: this.type == "radialBar" ? 10 : 2,
          colors: ["transparent"]
        },
        xaxis: {
          categories: this.labels.length ? this.formattedLables : this.formattedChartData,
          labels: {
            show: this.isChecked,
            style: {
              colors: this.$store.state.ui.darkMode ? "white" : this.textColor
            }
          }
        },
        yaxis: {
          labels: {
            show: this.showValues,
            style: {
              colors: this.$store.state.ui.darkMode ? "white" : this.textColor
            }
          }
        },
        fill: {
          opacity: 1
        }
      }

      if (this.type != "treemap" && this.type != "radialBar") {
        // eslint-disable-next-line vue/no-undef-properties
        this.chart_options["colors"] = this.column_colors
      }
      if (this.type == "radialBar" || this.type == "treemap") {
        // eslint-disable-next-line vue/no-undef-properties
        this.chart_options["labels"] = this.labels
      }
    },
    generateColors(isDarkMode) {
      this.chart_options = {
        dataLabels: {
          enabled: this.showDataLabels,
          showOn: "always",
          name: {
            show: true,
            color: "#888",
            fontSize: "13px"
          },
          value: {
            color: "#111",
            fontSize: "25px",
            show: true
          },
          total: {
            show: true,
            label: "Total",
            formatter: function () {
              return "100%"
            }
          }
        },

        chart: {
          foreColor: this.textColor,
          background: this.$store.state.ui.darkMode ? "#2b3c44" : "transparent"
        },
        title: {
          style: {
            color: this.$store.state.ui.darkMode ? "#FFF" : "#000"
          }
        },
        theme: {
          mode: isDarkMode ? "dark" : "light"
        },

        legend: {
          labels: {
            colors: this.$store.state.ui.darkMode ? "white" : this.textColor
          }
        },
        grid: {
          borderColor: "#dbdbdb"
        },
        xaxis: {
          labels: {
            show: this.isChecked,
            style: {
              colors: isDarkMode ? "white" : this.textColor
            }
          }
        },
        yaxis: {
          labels: {
            show: this.showValues,
            style: {
              colors: isDarkMode ? "white" : this.textColor
            }
          }
        }
      }
    }
  }
}
</script>
